/**
 * PWA bridge — service worker registration, update detection, and the
 * native "install" prompt. Framework-free module store so it can be
 * initialised from main.tsx (before React, before auth) and read from
 * anywhere via `useSyncExternalStore`.
 *
 * Update model (see VersionContext for the UI side):
 *   1. A new deploy ships a new sw.js. The browser installs it in the
 *      background and it sits in the "waiting" state (registerType: 'prompt').
 *   2. We flip `needRefresh` and the UI shows the "new version" banner.
 *   3. `applyUpdate()` tells the waiting worker to take over; workbox-window
 *      reloads the page once it does, so the user lands on fresh code.
 *
 * Install prompt model (see useInstallBanner):
 *   - Chromium browsers fire `beforeinstallprompt`. We stash the event so the
 *     banner can call `.prompt()` later from a user gesture.
 *   - iOS never fires it; the banner falls back to manual instructions.
 */
import { registerSW } from 'virtual:pwa-register';

// ── Minimal types for the non-standard BeforeInstallPromptEvent ──────────
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface PwaState {
  /** A new service worker is installed and waiting to take over. */
  needRefresh: boolean;
  /** The browser offered a native install prompt we can trigger. */
  canPromptInstall: boolean;
  /** The app is already running as an installed/standalone app. */
  isStandalone: boolean;
}

let state: PwaState = {
  needRefresh: false,
  canPromptInstall: false,
  isStandalone: detectStandalone(),
};

const listeners = new Set<() => void>();
let registration: ServiceWorkerRegistration | undefined;
let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | undefined;
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let initialised = false;

function emit() {
  listeners.forEach(l => l());
}

function setState(patch: Partial<PwaState>) {
  state = { ...state, ...patch };
  emit();
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    nav.standalone === true
  );
}

/** True when the worker is supported and currently controlling this page. */
export function isServiceWorkerControlled(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
}

/**
 * Register the service worker and start listening for the install prompt.
 * Safe to call more than once; only the first call does anything.
 */
export function initPwa(): void {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  // Native install prompt (Chromium on Android/desktop).
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    deferredInstallPrompt = e as BeforeInstallPromptEvent;
    setState({ canPromptInstall: true });
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    setState({ canPromptInstall: false, isStandalone: true });
  });

  if (!('serviceWorker' in navigator)) return;

  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      setState({ needRefresh: true });
    },
    onRegisteredSW(_url, r) {
      registration = r;
    },
    onRegisterError() {
      // Registration failing (e.g. unsupported browser, private mode) must
      // never affect the app — it simply runs as a normal web page.
    },
  });
}

// ── Store API ────────────────────────────────────────────────────────────

export function subscribePwa(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPwaState(): PwaState {
  return state;
}

/**
 * Ask the browser to check for a newer sw.js right now. Called by
 * VersionContext when the build hash poll notices a new deploy so the user
 * isn't left waiting for the browser's own update schedule.
 */
export async function checkForServiceWorkerUpdate(): Promise<void> {
  try {
    await registration?.update();
  } catch {
    // Offline or update check failed — nothing to do.
  }
}

/**
 * Move to the latest deployed version.
 *
 * - If a new worker is waiting, hand control to it; workbox-window reloads
 *   the page once it is controlling.
 * - Otherwise (no worker, or worker not yet updated) a plain reload is the
 *   best we can do; the caller is responsible for not looping on this.
 */
export async function applyUpdate(): Promise<void> {
  // Deploy detected by the build-hash poll but the new worker isn't waiting
  // yet: fetch it now and give it a moment to install, so the swap below can
  // happen in this one refresh instead of two.
  if (!state.needRefresh && registration && isServiceWorkerControlled()) {
    await checkForServiceWorkerUpdate();
    await waitForWaitingWorker(registration, 4000);
  }

  if ((state.needRefresh || registration?.waiting) && updateServiceWorker) {
    await updateServiceWorker(true);
    // Safety net: if the "controlling" event never fires (e.g. the waiting
    // worker was already purged), reload anyway after a short grace period.
    window.setTimeout(() => window.location.reload(), 2000);
    return;
  }
  window.location.reload();
}

/** Resolve once `registration.waiting` exists, or after `timeoutMs`. */
function waitForWaitingWorker(reg: ServiceWorkerRegistration, timeoutMs: number): Promise<void> {
  return new Promise(resolve => {
    if (reg.waiting) return resolve();
    const installing = reg.installing;
    if (!installing) return resolve();
    const timer = window.setTimeout(done, timeoutMs);
    function done() {
      window.clearTimeout(timer);
      installing?.removeEventListener('statechange', onChange);
      resolve();
    }
    function onChange() {
      if (installing?.state === 'installed' || installing?.state === 'redundant') done();
    }
    installing.addEventListener('statechange', onChange);
  });
}

/** Trigger the native install dialog. Must be called from a user gesture. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const evt = deferredInstallPrompt;
  if (!evt) return 'unavailable';
  deferredInstallPrompt = null;
  setState({ canPromptInstall: false });
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    return outcome;
  } catch {
    return 'dismissed';
  }
}

/** iPhone / iPad / iPod (including iPadOS reporting as Mac with touch). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}
